import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { Option } from '@utils/types/shared.ts';
import { formatOptions } from '@utils/constants/download-options.ts';

type Props = {
  handleExport(format: string): Promise<void>;
  modal: 'download' | 'txt' | 'pdf' | 'html';
  setModal: React.Dispatch<
    React.SetStateAction<'' | 'download' | 'txt' | 'pdf' | 'html'>
  >;
  includeTimestamps: boolean;
  setIncludeTimestamps: React.Dispatch<React.SetStateAction<boolean>>;
  includeSpeakers: boolean;
  setIncludeSpeakers: React.Dispatch<React.SetStateAction<boolean>>;
};

const DownloadDocumentForm = ({
  handleExport,
  modal,
  setModal,
  includeTimestamps,
  setIncludeTimestamps,
  includeSpeakers,
  setIncludeSpeakers
}: Props) => {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState<string | null>(null);

  const formatOption: Option[] = formatOptions.map(
    format =>
      ({
        label: format.name,
        value: format.key
      }) as Option
  );

  const onExport = async (key: string) => {
    try {
      setExporting(key);
      await handleExport(String(key));
    } finally {
      setExporting(null);
    }
  };

  return (
    <Modal
      isOpen={modal === 'download'}
      onClose={() => setModal('')}
      size="small"
      title={t('download-document')}
      caption={t(
        'using-the-settings-below-define-format-the-structure-and-format-of-the-file-you-are-about-to-download'
      )}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => setModal('')}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />
      ]}
    >
      <div className="w-full grid gap-4">
        {/* Options card */}
        <section className="rounded-2xl border border-hairline shadow-card bg-app-soft px-3 py-1.5 sm:px-4">
          <div className="divide-y divide-hairline">
            <div className="py-2">
              <Checkbox
                id="include_speakers"
                name="include_speakers"
                label={t('include-speakers')}
                checked={includeSpeakers}
                onChange={e => setIncludeSpeakers(e.target.checked)}
                variant="primary"
                wrapperClass="w-full"
                labelClass="text-sm font-medium"
              />
              <p className="text-xs mt-1">
                {t('adds-speaker-names-to-each-paragraph-if-available')}
              </p>
            </div>

            <div className="py-2">
              <Checkbox
                id="include_timestamps"
                name="include_timestamps"
                label={t('include-timestamps')}
                checked={includeTimestamps}
                onChange={e => setIncludeTimestamps(e.target.checked)}
                variant="primary"
                wrapperClass="w-full"
                labelClass="text-sm font-medium"
              />
              <p className="text-xs mt-1">
                {t('prepends-timecodes-to-sections-or-lines-based-on-format')}
              </p>
            </div>
          </div>
        </section>

        {/* Formats */}
        <section>
          <h3 className="text-sm font-semibold mb-3">{t('format')}</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {formatOption.map(option => {
              const isActive = exporting === option.value;
              return (
                <Button
                  key={String(option.value)}
                  className="w-full h-11 !justify-center text-sm font-semibold"
                  label={
                    isActive ? t('downloading') + '…' : String(option.label)
                  }
                  disabled={!!exporting}
                  variant="secondary"
                  onClick={() => onExport(String(option.value))}
                />
              );
            })}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default DownloadDocumentForm;
